import React, { useCallback, useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";
import { dmY } from "@/lib/dateFmt";
import { sortPaymentModes } from "@/lib/paymentModes";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function isoNDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
function inr(n) { return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function CashExpenses() {
    const [rows, setRows] = useState([]);
    const [fromDate, setFromDate] = useState(isoNDaysAgo(30));
    const [toDate, setToDate] = useState(todayISO());
    const [busy, setBusy] = useState(false);
    const [modes, setModes] = useState([]);
    const [dialog, setDialog] = useState(null);   // {date, amount, description, payment_mode, id?}

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [r, m] = await Promise.all([
                api.get(`/expenses?from_date=${fromDate}&to_date=${toDate}`),
                api.get("/payment-modes"),
            ]);
            setRows(r.data || []);
            setModes(m.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }, [fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    const openNew = () => setDialog({ date: todayISO(), amount: "", description: "", payment_mode: "Cash" });
    const openEdit = (row) => setDialog({ ...row, amount: String(row.amount) });

    const save = async () => {
        if (!dialog?.description?.trim()) { toast.error("Enter a description"); return; }
        const amt = parseFloat(dialog.amount);
        if (!amt || amt <= 0) { toast.error("Enter an amount > 0"); return; }
        try {
            const body = {
                date: dialog.date,
                amount: amt,
                description: dialog.description.trim(),
                payment_mode: dialog.payment_mode || "Cash",
            };
            if (dialog.id) {
                await api.patch(`/expenses/${dialog.id}`, body);
                toast.success("Expense updated");
            } else {
                await api.post("/expenses", body);
                toast.success("Expense recorded");
            }
            setDialog(null);
            load();
            window.dispatchEvent(new CustomEvent("micm-cash-updated"));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (row) => {
        if (!window.confirm(`Delete expense of ₹${inr(row.amount)} on ${dmY(row.date)}?`)) return;
        try {
            await api.delete(`/expenses/${row.id}`);
            toast.success("Expense deleted");
            load();
            window.dispatchEvent(new CustomEvent("micm-cash-updated"));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

    return (
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900">
                        <Receipt size={22} weight="fill" className="text-amber-700" />
                        Cash Expenses
                    </h1>
                    <p className="text-sm text-slate-500">Only cash-mode expenses reduce Cash In Hand.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                        From
                        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                               data-testid="expense-from-date"
                               className="rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-amber-400" />
                    </label>
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                        To
                        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                               data-testid="expense-to-date"
                               className="rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-amber-400" />
                    </label>
                    <button type="button" onClick={openNew}
                            data-testid="expense-add-btn"
                            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-amber-700">
                        <Plus size={14} weight="bold" /> Record Expense
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-sm" data-testid="expenses-table">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-left">Mode</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {busy && (<tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>)}
                        {!busy && rows.length === 0 && (
                            <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No expenses in this range.</td></tr>
                        )}
                        {!busy && rows.map((r) => (
                            <tr key={r.id} className="hover:bg-slate-50">
                                <td className="px-3 py-2">{dmY(r.date)}</td>
                                <td className="px-3 py-2">{r.description}</td>
                                <td className="px-3 py-2">
                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-900">
                                        {r.payment_mode || "Cash"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums font-semibold">₹{inr(r.amount)}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => openEdit(r)} className="mr-1 rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`expense-edit-${r.id}`}>
                                        <PencilSimple size={14} />
                                    </button>
                                    <button onClick={() => del(r)} className="rounded p-1 text-rose-500 hover:bg-rose-50" data-testid={`expense-delete-${r.id}`}>
                                        <Trash size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {!busy && rows.length > 0 && (
                        <tfoot className="bg-slate-50 font-bold">
                            <tr>
                                <td className="px-3 py-2" colSpan={3}>Total</td>
                                <td className="px-3 py-2 text-right tabular-nums" data-testid="expenses-total">₹{inr(total)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {dialog && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
                     role="dialog" aria-modal="true" data-testid="expense-dialog">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
                            <div className="text-sm font-bold uppercase tracking-wider text-slate-700">
                                {dialog.id ? "Edit Expense" : "Record Expense"}
                            </div>
                            <button onClick={() => setDialog(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X size={14} /></button>
                        </div>
                        <div className="space-y-3 px-5 py-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Date</label>
                                <input type="date" value={dialog.date} onChange={(e) => setDialog({ ...dialog, date: e.target.value })}
                                       data-testid="expense-input-date"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Amount (₹)</label>
                                <input type="number" min="0" step="0.01" value={dialog.amount}
                                       onChange={(e) => setDialog({ ...dialog, amount: e.target.value })}
                                       data-testid="expense-input-amount"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Description</label>
                                <input type="text" value={dialog.description}
                                       onChange={(e) => setDialog({ ...dialog, description: e.target.value })}
                                       placeholder="e.g. Office electricity, courier, tea"
                                       data-testid="expense-input-description"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Payment Mode</label>
                                <select value={dialog.payment_mode}
                                        onChange={(e) => setDialog({ ...dialog, payment_mode: e.target.value })}
                                        data-testid="expense-input-mode"
                                        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-400">
                                    <option value="Cash">Cash</option>
                                    {/* v1.29.20 — Sort A-Z + Title Case via shared helper. */}
                                    {sortPaymentModes(modes).filter((m) => (m.name || "").toLowerCase() !== "cash").map((m) => (
                                        <option key={m.id || m.name} value={m.name}>{m.name}</option>
                                    ))}
                                </select>
                                <div className="mt-1 text-[11px] text-slate-500">Only Cash-mode expenses reduce Cash In Hand.</div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
                            <button type="button" onClick={() => setDialog(null)}
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button type="button" onClick={save}
                                    data-testid="expense-save-btn"
                                    className="rounded-md bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">
                                {dialog.id ? "Save Changes" : "Record Expense"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
